// apps/admin-dashboard/src/pages/DiscographyFormPage.tsx
import { FormEvent, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Images } from 'lucide-react';
import type { DiscographyRelease, Member, ReleaseType } from '@yp/types';
import { useAuth } from '../context/AuthContext';
import {
  getAdminReleaseById, createRelease, updateRelease,
  getAdminMembers, type ReleasePayload,
} from '../lib/api';
import ImagePickerModal from '../components/ImagePickerModal';

const RELEASE_TYPES: ReleaseType[] = ['single','mini_album','album','digital','collaboration'];
const TYPE_LABELS: Record<ReleaseType, string> = {
  single: 'Single', mini_album: 'Mini Album', album: 'Album',
  digital: 'Digital', collaboration: 'Collaboration',
};
const STREAMING_PLATFORMS = ['spotify','apple_music','youtube','joox','soundcloud'];

function toSlug(s: string) {
  return s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
}

interface MemberRow {
  member_id: string;
  role_in_release: string;
  sort_order: number;
}

const DEFAULT_FORM: Partial<ReleasePayload> = {
  slug: '', title: '', type: 'single',
  cover_url: '', release_date: '', date_precision: 'day',
  streaming_links: {}, description: '',
  is_published: false, order_index: 0,
};

export default function DiscographyFormPage() {
  const { id } = useParams<{ id?: string }>();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const { accessToken } = useAuth();

  const [form, setForm] = useState<Partial<ReleasePayload>>(DEFAULT_FORM);
  const [memberRows, setMemberRows] = useState<MemberRow[]>([]);
  const [allMembers, setAllMembers] = useState<Member[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(isEdit);
  const [error, setError] = useState('');
  const [coverPickerOpen, setCoverPickerOpen] = useState(false);

  useEffect(() => {
    if (!accessToken) return;
    getAdminMembers(accessToken)
      .then(({ data }) => setAllMembers(data))
      .catch(() => {/* members list unavailable */});
    if (isEdit && id) {
      getAdminReleaseById(accessToken, id)
        .then((release: DiscographyRelease) => {
          setForm({
            slug: release.slug, title: release.title, type: release.type,
            cover_url: release.cover_url ?? '', release_date: release.release_date ?? '',
            date_precision: release.date_precision, streaming_links: release.streaming_links,
            description: release.description ?? '', is_published: release.is_published,
            order_index: release.order_index,
          });
          setMemberRows((release.members ?? []).map((m) => ({
            member_id: m.member_id,
            role_in_release: m.role_in_release ?? '',
            sort_order: m.sort_order,
          })));
        })
        .finally(() => setIsFetching(false));
    }
  }, [accessToken, isEdit, id]);

  function setField<K extends keyof ReleasePayload>(key: K, value: ReleasePayload[K]) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  function setStreamingLink(platform: string, url: string) {
    setForm((prev) => ({
      ...prev,
      streaming_links: { ...(prev.streaming_links ?? {}), [platform]: url },
    }));
  }

  function addMemberRow() {
    setMemberRows((prev) => [...prev, { member_id: '', role_in_release: '', sort_order: prev.length }]);
  }

  function updateMemberRow(index: number, field: keyof MemberRow, value: string | number) {
    setMemberRows((prev) => prev.map((r, i) => i === index ? { ...r, [field]: value } : r));
  }

  function removeMemberRow(index: number) {
    setMemberRows((prev) => prev.filter((_, i) => i !== index));
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!accessToken) return;
    setError('');
    setIsLoading(true);
    try {
      const payload: ReleasePayload = {
        ...form as ReleasePayload,
        members: memberRows.filter((r) => r.member_id).map((r, i) => ({
          member_id: r.member_id,
          role_in_release: r.role_in_release || undefined,
          sort_order: i,
        })),
      };
      if (isEdit && id) {
        await updateRelease(accessToken, id, payload);
      } else {
        await createRelease(accessToken, payload);
      }
      navigate('/discography');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setIsLoading(false);
    }
  }

  const inputCls = 'w-full rounded-lg border border-[var(--color-border)] bg-black/20 px-3 py-2 text-sm text-gray-200 placeholder:text-gray-600 focus:border-pink-500/50 focus:outline-none';
  const labelCls = 'mb-1.5 block text-xs font-medium text-gray-400';

  if (isFetching) {
    return (
      <div className="flex justify-center py-20">
        <div className="h-6 w-6 animate-spin rounded-full border-2 border-pink-500 border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-2xl">
      <div className="mb-6 flex items-center gap-3">
        <button type="button" onClick={() => navigate('/discography')}
          className="rounded-lg p-1.5 text-gray-400 hover:bg-white/5 hover:text-gray-200">
          <ArrowLeft size={18} />
        </button>
        <h1 className="text-xl font-semibold text-gray-100">
          {isEdit ? 'Edit Release' : 'Add Release'}
        </h1>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Cover */}
        <div>
          <label className={labelCls}>Cover Image</label>
          <div className="flex gap-3">
            {form.cover_url && (
              <img src={form.cover_url} alt="cover" className="h-20 w-20 rounded-lg object-cover" />
            )}
            <button type="button" onClick={() => setCoverPickerOpen(true)}
              className="flex cursor-pointer flex-col items-center justify-center gap-1 rounded-lg border border-dashed border-[var(--color-border)] px-4 py-3 text-xs text-gray-500 hover:border-pink-500/30 hover:text-pink-400">
              <Images size={16} /> Pilih Gambar
            </button>
          </div>
        </div>

        {/* Title + slug */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className={labelCls}>Title <span className="text-red-400">*</span></label>
            <input type="text" required value={form.title ?? ''}
              onChange={(e) => {
                const title = e.target.value;
                setForm((prev) => ({ ...prev, title, slug: isEdit ? prev.slug : toSlug(title) }));
              }}
              className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Slug <span className="text-red-400">*</span></label>
            <input
              type="text"
              required
              value={form.slug ?? ''}
              readOnly={isEdit}
              onChange={isEdit ? undefined : (e) => setField('slug', e.target.value)}
              className={`${inputCls} ${isEdit ? 'cursor-not-allowed opacity-60' : ''}`}
            />
          </div>
        </div>

        {/* Type */}
        <div>
          <label className={labelCls}>Release Type</label>
          <select value={form.type ?? 'single'}
            onChange={(e) => setField('type', e.target.value as ReleaseType)}
            className={`${inputCls} bg-[var(--color-surface)]`}>
            {RELEASE_TYPES.map((t) => <option key={t} value={t}>{TYPE_LABELS[t]}</option>)}
          </select>
        </div>

        {/* Release date + date_precision */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className={labelCls}>Release Date</label>
            <input type="date" value={form.release_date ?? ''}
              onChange={(e) => setField('release_date', e.target.value || undefined)}
              className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Date Precision</label>
            <select value={form.date_precision ?? 'day'}
              onChange={(e) => setField('date_precision', e.target.value as 'day'|'month'|'year')}
              className={`${inputCls} bg-[var(--color-surface)]`}>
              <option value="day">Day (exact)</option>
              <option value="month">Month only</option>
              <option value="year">Year only</option>
            </select>
          </div>
        </div>

        {/* Description */}
        <div>
          <label className={labelCls}>Description</label>
          <textarea rows={3} value={form.description ?? ''}
            onChange={(e) => setField('description', e.target.value || undefined)}
            className={`${inputCls} resize-none`} />
        </div>

        {/* Streaming links */}
        <div>
          <label className={labelCls}>Streaming Links</label>
          <div className="space-y-2">
            {STREAMING_PLATFORMS.map((platform) => (
              <div key={platform} className="flex items-center gap-2">
                <span className="w-24 text-xs text-gray-400 capitalize">{platform.replace('_', ' ')}</span>
                <input type="url" placeholder={`https://...`}
                  value={(form.streaming_links ?? {})[platform] ?? ''}
                  onChange={(e) => setStreamingLink(platform, e.target.value)}
                  className="flex-1 rounded-lg border border-[var(--color-border)] bg-black/20 px-3 py-1.5 text-xs text-gray-300 focus:outline-none focus:border-pink-500/50" />
              </div>
            ))}
          </div>
        </div>

        {/* Member assignment */}
        <div>
          <label className={labelCls}>Members (senbatsu / participating)</label>
          <div className="space-y-2">
            {memberRows.map((row, i) => (
              <div key={i} className="flex items-center gap-2">
                <select value={row.member_id}
                  onChange={(e) => updateMemberRow(i, 'member_id', e.target.value)}
                  className="rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] px-2 py-1.5 text-xs text-gray-300 focus:outline-none flex-1">
                  <option value="">— select member —</option>
                  {allMembers.map((m) => (
                    <option key={m.id} value={m.id}>{m.nickname}</option>
                  ))}
                </select>
                <input type="text" placeholder="role (e.g. senbatsu)"
                  value={row.role_in_release}
                  onChange={(e) => updateMemberRow(i, 'role_in_release', e.target.value)}
                  className="rounded-lg border border-[var(--color-border)] bg-black/20 px-2 py-1.5 text-xs text-gray-300 focus:outline-none w-32" />
                <button type="button" onClick={() => removeMemberRow(i)}
                  className="text-xs text-gray-500 hover:text-red-400">&#x2715;</button>
              </div>
            ))}
            <button type="button" onClick={addMemberRow}
              className="text-xs text-pink-400 hover:underline">+ Add member</button>
          </div>
        </div>

        {/* Published + order */}
        <div className="flex items-center gap-6">
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={form.is_published ?? false}
              onChange={(e) => setField('is_published', e.target.checked)}
              className="h-4 w-4 rounded accent-pink-500" />
            <span className="text-sm text-gray-300">Published</span>
          </label>
          <div className="flex items-center gap-2">
            <label className="text-xs text-gray-400">Order:</label>
            <input type="number" min={0} value={form.order_index ?? 0}
              onChange={(e) => setField('order_index', Number(e.target.value))}
              className="w-16 rounded-lg border border-[var(--color-border)] bg-black/20 px-2 py-1 text-xs text-gray-200 focus:outline-none" />
          </div>
        </div>

        {error && <p className="rounded-lg bg-red-500/10 px-3 py-2 text-xs text-red-400">{error}</p>}

        <div className="flex gap-3 pt-2">
          <button type="button" onClick={() => navigate('/discography')}
            className="rounded-lg border border-[var(--color-border)] px-4 py-2 text-sm text-gray-400 hover:bg-white/5">
            Cancel
          </button>
          <button type="submit" disabled={isLoading}
            className="rounded-lg px-6 py-2 text-sm font-medium text-white disabled:opacity-60"
            style={{ backgroundColor: 'var(--yp-pink)' }}>
            {isLoading ? 'Saving…' : isEdit ? 'Save Changes' : 'Add Release'}
          </button>
        </div>
      </form>

      <ImagePickerModal open={coverPickerOpen} onClose={() => setCoverPickerOpen(false)}
        onSelect={(url) => setField('cover_url', url)} defaultFolder="discography" />
    </div>
  );
}
